import React, { useState, useEffect } from 'react';
import { Settings, Save, AlertCircle } from 'lucide-react';
import { supabase } from './supabaseClient';

// Identity text fields — stored as-is (no conversion)
const TEXT_FIELDS = [
  { key: 'gestor_code',     label: 'CÓDIGO GESTOR',  desc: 'Código de Gestor de Encomiendas'              },
  { key: 'aduana_code',     label: 'CÓDIGO ADUANA',  desc: 'Código de la aduana (ej. 03)'                 },
  { key: 'gestor_name',     label: 'NOMBRE GESTOR',  desc: 'Nombre legal del gestor para firma'           },
  { key: 'registro_prefix', label: 'NO. REGISTRO',   desc: 'Prefijo del registro de mercancías (ej. 4-)' },
];

// Field definitions — pct:true means stored as decimal (0.30), displayed as % (30)
const FIELDS = [
  { key: 'threshold_usd', label: 'Umbral CIF',  desc: 'Valor mínimo CIF para aplicar DAI',       step: '0.01',  suffix: 'USD', pct: false },
  { key: 'dai_rate',      label: 'Tasa DAI',     desc: 'Derecho Arancelario a la Importación',    step: '0.001', suffix: '%',   pct: true  },
  { key: 'iva_rate',      label: 'Tasa IVA',     desc: 'Impuesto al Valor Agregado',              step: '0.001', suffix: '%',   pct: true  },
  { key: 'vts_fee',       label: 'Tarifa VTS',   desc: 'Verificación de Tránsito y Seguridad',   step: '0.01',  suffix: 'USD', pct: false },
  { key: 'its_fee',       label: 'Tarifa ITS',   desc: 'Inspección de Transporte Seguro',         step: '0.01',  suffix: 'USD', pct: false },
  { key: 'flete_rate',    label: 'Flete',        desc: 'Porcentaje de flete sobre FOB',           step: '0.001', suffix: '%',   pct: true  },
  { key: 'seguro_rate',   label: 'Seguro',       desc: 'Porcentaje de seguro sobre FOB',          step: '0.001', suffix: '%',   pct: true  },
];

// Convert stored decimal → display string (strips trailing zeros for cleanliness)
const toDisplay = (raw, pct) => {
  if (raw === null || raw === undefined) return '';
  return pct
    ? String((raw * 100).toFixed(3)).replace(/\.?0+$/, '') || '0'
    : String(raw);
};

const CustomsSettings = ({ user, userPermissions }) => {
  const [config, setConfig]     = useState(null);
  const [form, setForm]         = useState({});
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState('');
  const [toastError, setToastError] = useState(false);

  const showToast = (msg, isError = false) => {
    setToast(msg);
    setToastError(isError);
    setTimeout(() => setToast(''), 4000);
  };

  // ── Fetch single config row on mount ──────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('customs_config')
          .select('*')
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        setConfig(data);
        if (data) {
          const initial = {};
          for (const f of FIELDS) {
            initial[f.key] = toDisplay(data[f.key], f.pct);
          }
          initial.gestor_code     = data.gestor_code     ?? 'GE0000';
          initial.aduana_code     = data.aduana_code     ?? '03';
          initial.gestor_name     = data.gestor_name     ?? 'NOMBRE DEL GESTOR';
          initial.registro_prefix = data.registro_prefix ?? '4-';
          setForm(initial);
          console.log('[customsConfig] loaded — id:', data.id, 'threshold_usd:', data.threshold_usd);
        }
      } catch (err) {
        console.error('[customsConfig] load error:', err);
        showToast('Error al cargar configuración.', true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── Save — UPDATE the single row in place, never INSERT ──────────────────
  const handleSave = async () => {
    if (!config?.id) return;
    setSaving(true);
    try {
      const payload = {};
      for (const f of FIELDS) {
        payload[f.key] = f.pct
          ? (parseFloat(form[f.key]) || 0) / 100
          : parseFloat(form[f.key]) || 0;
      }
      payload.gestor_code     = form.gestor_code     ?? '';
      payload.aduana_code     = form.aduana_code     ?? '';
      payload.gestor_name     = form.gestor_name     ?? '';
      payload.registro_prefix = form.registro_prefix ?? '';
      payload.updated_by = user?.username || user?.email || 'unknown';
      payload.updated_at = new Date().toISOString();

      const { error } = await supabase
        .from('customs_config')
        .update(payload)
        .eq('id', config.id);
      if (error) throw error;

      setConfig(prev => ({ ...prev, ...payload }));
      console.log('[customsConfig] saved — by:', payload.updated_by);
      showToast('Configuración guardada exitosamente.');
    } catch (err) {
      console.error('[customsConfig] save error:', err);
      showToast('Error al guardar: ' + (err.message || ''), true);
    } finally {
      setSaving(false);
    }
  };

  // ── Loading / not found states ─────────────────────────────────────────────
  if (loading) {
    return (
      <div className="tile max-w-2xl mx-auto" style={{ padding: '40px', textAlign: 'center' }}>
        <span className="metric-label">Cargando configuración…</span>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="tile max-w-2xl mx-auto" style={{ padding: '40px', textAlign: 'center' }}>
        <AlertCircle size={28} style={{ margin: '0 auto 8px', opacity: 0.4 }} />
        <span className="metric-label">No se encontró configuración aduanera. Contacte al administrador.</span>
      </div>
    );
  }

  const canEdit = !!userPermissions?.canEditCustomsConfig;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '16px', right: '16px', zIndex: 9999,
          background: 'var(--bg)', border: '1px solid var(--border)',
          borderRadius: '10px', padding: '12px 20px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
          fontSize: '14px', fontWeight: 500,
          color: toastError ? 'var(--red, #dc2626)' : 'inherit',
        }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="tile" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Settings size={24} style={{ color: 'var(--blue)', opacity: 0.7 }} />
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>Configuración Aduanera</h2>
            <p className="metric-sub" style={{ margin: '4px 0 0' }}>
              Tasas e impuestos aplicados en la declaración DUA para SIDUNEA.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="tile">
        {/* Identity fields — gestor, aduana, name, registro */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '20px', marginBottom: '24px' }}>
          {TEXT_FIELDS.map(f => (
            <div key={f.key}>
              <label style={{
                fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)',
                textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '2px',
              }}>
                {f.label}
              </label>
              <p className="metric-sub" style={{ margin: '0 0 6px', fontSize: '11px' }}>{f.desc}</p>
              <input
                type="text"
                value={form[f.key] ?? ''}
                onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                disabled={!canEdit}
                style={{
                  width: '100%', padding: '8px 10px',
                  fontSize: '15px', fontWeight: 600,
                  border: '1px solid var(--border)', borderRadius: '8px',
                  background: canEdit ? 'var(--bg)' : 'var(--bg-soft, #f8f9fa)',
                  color: 'var(--text)',
                  cursor: canEdit ? 'auto' : 'not-allowed',
                }}
              />
            </div>
          ))}
        </div>

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '20px' }}>
          {FIELDS.map(f => (
            <div key={f.key}>
              <label style={{
                fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)',
                textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '2px',
              }}>
                {f.label}
              </label>
              <p className="metric-sub" style={{ margin: '0 0 6px', fontSize: '11px' }}>{f.desc}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="number"
                  step={f.step}
                  min="0"
                  value={form[f.key] ?? ''}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                  disabled={!canEdit}
                  style={{
                    width: '110px', padding: '8px 10px',
                    fontSize: '15px', fontWeight: 600,
                    border: '1px solid var(--border)', borderRadius: '8px',
                    background: canEdit ? 'var(--bg)' : 'var(--bg-soft, #f8f9fa)',
                    color: 'var(--text)',
                    cursor: canEdit ? 'auto' : 'not-allowed',
                  }}
                />
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  {f.suffix}
                </span>
              </div>
            </div>
          ))}
        </div>
        </div>

        {/* Audit row — read-only */}
        <div style={{
          marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--border)',
          display: 'flex', gap: '24px', fontSize: '12px', color: 'var(--text-tertiary)',
          flexWrap: 'wrap',
        }}>
          <span>
            Última actualización:{' '}
            <strong>{config.updated_at ? new Date(config.updated_at).toLocaleString('es-SV') : '—'}</strong>
          </span>
          <span>Por: <strong>{config.updated_by || '—'}</strong></span>
        </div>

        {/* Save button — always rendered; disabled for non-editors */}
        <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !canEdit}
            className="btn btn-primary"
            style={{
              fontSize: '14px', padding: '10px 24px',
              display: 'flex', alignItems: 'center', gap: '8px',
              opacity: canEdit ? 1 : 0.45,
              cursor: canEdit ? 'pointer' : 'not-allowed',
            }}
          >
            <Save size={16} />
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
          {!canEdit && (
            <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
              Solo administradores y gerentes pueden editar esta configuración.
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomsSettings;
